import { useRef, useState } from 'react';
import { Box, Drawer, IconButton, Popover, alpha, useMediaQuery, useTheme } from '@mui/material';
import BellIcon from '@mui/icons-material/Notifications';
import { FOCUS_RING } from '../../../theme/tokens';
import { useUnreadCount } from '../hooks';
import {
  BADGE_FONT_SIZE,
  BADGE_MAX_DISPLAY_COUNT,
  BADGE_SIZE,
  BELL_BUTTON_SIZE,
  BELL_ICON_FONT_SIZE,
  PANEL_BORDER_RADIUS,
  PANEL_MAX_HEIGHT,
  PANEL_POPOVER_MIN_WIDTH,
  PANEL_SHADOW,
  PANEL_SHADOW_DARK,
} from '../designTokens';
import { NotificationPanel } from './NotificationPanel';

/** "3" up to the cap, then "99+" — the pill never grows wide enough to break the bell's square. */
function badgeText(unread: number): string {
  return unread > BADGE_MAX_DISPLAY_COUNT ? `${BADGE_MAX_DISPLAY_COUNT}+` : String(unread);
}

/**
 * The `TopBar` bell (IAN-25, AC-27): enabled for every authenticated user,
 * no permission key — an inbox is not an admin surface. Replaces the disabled
 * "coming soon" placeholder entirely.
 *
 * The count pill uses `statusSoft.info` / `statusInk.info`, never a red dot —
 * this is not an alert surface, and red is reserved for failure in this
 * product (SPEC "Bell").
 *
 * At ≥768 the panel is a popover anchored to the bell; below that a popover
 * this wide would be clipped by the viewport, so it becomes a full-width
 * bottom sheet instead (AC-31). Either surface mounts `NotificationPanel`
 * only while open — see that component's own doc for why that matters.
 */
export function NotificationBell() {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isWide = useMediaQuery(`(min-width: ${PANEL_POPOVER_MIN_WIDTH}px)`);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const { unread, setUnread } = useUnreadCount();

  const focusRing = FOCUS_RING(alpha(theme.palette.primary.main, 0.35));
  const handleClose = () => setOpen(false);
  const ariaLabel = unread > 0 ? `Notifications, ${unread} unread` : 'Notifications';

  return (
    <>
      <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
        <IconButton
          ref={buttonRef}
          aria-label={ariaLabel}
          // Without these a screen reader announces a plain button and gives no
          // way to know the panel opened — the bell is the one control in the
          // top bar that reveals a surface rather than performing an action.
          aria-haspopup="dialog"
          aria-expanded={open}
          size="small"
          onClick={() => setOpen(true)}
          sx={{
            width: BELL_BUTTON_SIZE,
            height: BELL_BUTTON_SIZE,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            borderRadius: 1,
            color: 'text.secondary',
            '&.Mui-focusVisible': { outline: 'none', boxShadow: focusRing },
          }}
        >
          <BellIcon sx={{ fontSize: BELL_ICON_FONT_SIZE }} />
        </IconButton>
        {unread > 0 && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: -4,
              right: -4,
              minWidth: BADGE_SIZE,
              height: BADGE_SIZE,
              px: '3px',
              borderRadius: 999,
              bgcolor: 'statusSoft.info',
              color: 'statusInk.info',
              fontSize: BADGE_FONT_SIZE,
              fontWeight: 700,
              lineHeight: `${BADGE_SIZE}px`,
              textAlign: 'center',
              border: '1px solid',
              borderColor: 'background.paper',
            }}
          >
            {badgeText(unread)}
          </Box>
        )}
      </Box>

      {isWide ? (
        <Popover
          open={open}
          anchorEl={buttonRef.current}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: {
                mt: 0.5,
                borderRadius: `${PANEL_BORDER_RADIUS}px`,
                boxShadow: isDarkMode ? PANEL_SHADOW_DARK : PANEL_SHADOW,
                maxHeight: PANEL_MAX_HEIGHT,
                overflow: 'hidden',
              },
            },
          }}
        >
          {open && <NotificationPanel onClose={handleClose} onUnreadChange={setUnread} variant="popover" />}
        </Popover>
      ) : (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={handleClose}
          slotProps={{
            paper: {
              sx: {
                borderTopLeftRadius: `${PANEL_BORDER_RADIUS}px`,
                borderTopRightRadius: `${PANEL_BORDER_RADIUS}px`,
                maxHeight: '80vh',
                overflow: 'hidden',
              },
            },
          }}
        >
          {open && <NotificationPanel onClose={handleClose} onUnreadChange={setUnread} variant="sheet" />}
        </Drawer>
      )}
    </>
  );
}

export default NotificationBell;
